import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Text } from '@radix-ui/themes'
import { MdAddCircleOutline } from '@react-icons/all-files/md/MdAddCircleOutline'
import type { MentionUser } from 'types/discussion'
import styles from './mention-list.module.css'

export type MentionListProps = {
  items: MentionUser[]
  loading?: boolean
  command: (item: { id: number | string; label: string }) => void
  query?: string
  onInvite?: (query: string) => void
}

export type MentionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  ({ items, loading = false, command, query = '', onInvite }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    useEffect(() => {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' })
    }, [selectedIndex])

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index]
        if (!item) return
        command({ id: item.id, label: item.label })
      },
      [items, command],
    )

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (items.length === 0) {
            if (onInvite && event.key === 'Enter') {
              onInvite(query)
              return true
            }
            return false
          }

          if (event.key === 'ArrowUp') {
            setSelectedIndex(prev => (prev <= 0 ? items.length - 1 : prev - 1))
            return true
          }

          if (event.key === 'ArrowDown') {
            setSelectedIndex(prev => (prev >= items.length - 1 ? 0 : prev + 1))
            return true
          }

          if (event.key === 'Enter') {
            selectItem(selectedIndex)
            return true
          }

          return false
        },
      }),
      [selectedIndex, selectItem, items.length, onInvite, query],
    )

    if (loading) {
      return (
        <div className={styles.mentionList}>
          <div className={styles.empty}>Loading…</div>
        </div>
      )
    }

    if (items.length === 0) {
      if (onInvite) {
        return (
          <div className={styles.mentionList}>
            <Flex
              align="center"
              justify="between"
              gap="3"
              p="2"
              className={styles.inviteEmpty}
            >
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">
                  No user found
                </Text>
                <Text size="1" color="gray">
                  To mention this user, invite them to this DISCO
                </Text>
              </Flex>
              <Button
                type="button"
                data-mention-invite=""
                color="gray"
                highContrast
                onClick={() => onInvite(query)}
              >
                <MdAddCircleOutline size={16} />
                Invite
              </Button>
            </Flex>
          </div>
        )
      }
      return (
        <div className={styles.mentionList}>
          <div className={styles.empty}>No users found</div>
        </div>
      )
    }

    return (
      <div className={styles.mentionList}>
        {items.map((item, index) => (
          <button
            key={item.id}
            ref={el => {
              itemRefs.current[index] = el
            }}
            className={`${styles.item} ${
              index === selectedIndex ? styles.itemSelected : ''
            }`}
            onClick={() => selectItem(index)}
            type="button"
          >
            <span className={styles.name}>{item.display ?? item.label}</span>
            {item.description && (
              <span className={styles.email}>{item.description}</span>
            )}
          </button>
        ))}
      </div>
    )
  },
)

MentionList.displayName = 'MentionList'
